import cx from "classnames";
import { Icon } from "common/Icon";
import { CTA } from "common/styleguide/CTA";
import { SectionBox } from "common/styleguide/SectionBox";
import { milestoneActions, milestoneSelectors } from "data/resources/milestones";
import { Completed } from "features/challenges/ChallengeShowPage";
import { useHover } from "lib/hooks/useHover";
import { featureNames } from "lib/localization";
import { showModal } from "lib/modalUtils";
import React, { useState } from "react";
import { defineMessages } from "react-intl";
import { connect } from "react-redux";
import { CREATE_EDIT_MILESTONE_MODAL } from "../../modals/CreateEditMilestoneModal";
import styles from "./MilestoneConfig.scss";

export const messages = defineMessages({
  explainer: {
    id: "MilestoneConfig.explainer",
    defaultMessage: "Milestones allow you to celebrate participants’ progress along the way.",
  },
  add: {
    id: "MilestoneConfig.add",
    defaultMessage: "Add Milestone",
  },
  target: {
    id: "MilestoneConfig.target",
    defaultMessage: "Target",
  },
  percentage: {
    id: "MilestoneConfig.percentage",
    defaultMessage: "Percentage",
  },
  deleteConfirm: {
    id: "MilestoneConfig.deleteConfirm",
    defaultMessage: "Are you sure you want to delete this Milestone? This action cannot be undone",
  },
});

export const _MilestoneConfig = ({
  goal,
  goalType,
  goalParams,
  milestones,
  deleteMilestone,
  disabled,
  fm,
  metricUnits,
}) => {
  const sorted = milestones.sortBy((m) => m.targetValue);
  const newTargetType = milestones.first()
    ? milestones.first().targetType
    : goalParams.get("has_target")
    ? "percent"
    : "value";
  return (
    <SectionBox title={fm(featureNames.milestones)} noPadding>
      <div className={styles.cardBody}>
        {milestones.size === 0 ? (
          <div className={styles.empty}>{fm(messages.explainer)}</div>
        ) : (
          sorted
            .map((m) => (
              <MilestoneRow
                key={m.id}
                m={m}
                deleteMilestone={deleteMilestone}
                goal={goal}
                goalType={goalType}
                goalParams={goalParams}
                disabled={disabled}
                fm={fm}
                metricUnits={metricUnits}
              />
            ))
            .toIndexedSeq()
        )}
        <div className={styles.milestoneCTA}>
          <CTA
            disabled={disabled}
            autoWidth
            secondary
            margins={false}
            onClick={() =>
              !disabled &&
              showModal(CREATE_EDIT_MILESTONE_MODAL, {
                goal,
                goalType,
                goalParams,
                targetType: newTargetType,
              })
            }
          >
            <Icon i="md-add-circle" size={16} style={{ marginRight: "10px" }} />
            {fm(messages.add)}
          </CTA>
        </div>
      </div>
    </SectionBox>
  );
};

const MilestoneRow = ({ m, goal, goalType, goalParams, deleteMilestone, disabled, fm, metricUnits }) => {
  const [hoverRef, isHovered] = useHover();
  const [deleting, setDeleting] = useState(false);

  const onDeleteClick = (mId) => {
    if (deleting) return;
    window.alert(fm(messages.deleteConfirm));
    setDeleting(true);

    deleteMilestone(mId).finally(() => {
      setDeleting(false);
    });
  };

  const value =
    m.targetType === "percent" ? (
      `${m.targetValue}%`
    ) : (
      <Completed goalType={goal.get("goalType")} value={m.targetValue} fm={fm} noLabel />
    );
  return (
    <div ref={hoverRef} className={cx(styles.milestoneRow, isHovered && styles.hovered)}>
      <div className={styles.left}>
        <h3 className={styles.title}>{m.title}</h3>
        <div className={styles.overview}>{m.overview}</div>
        <div className={styles.completed}>
          {value}
          <div className={cx(styles.actions, isHovered && styles.hovered)}>
            {!disabled && (
              <Icon
                size={18}
                i="md-create"
                onClick={() =>
                  showModal(CREATE_EDIT_MILESTONE_MODAL, {
                    milestone: m,
                    goal,
                    goalType,
                    goalParams,
                  })
                }
              />
            )}
            <Icon size={18} i="md-trash" onClick={() => onDeleteClick(m.id)} />
          </div>
        </div>
      </div>
      <img src={m.badge} height="80" width="80" />
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { id } = ownProps.goal;
  return {
    milestones: milestoneSelectors.byGoalId(state, id),
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    deleteMilestone: (id) => dispatch(milestoneActions.delete(id)),
  };
};

export const MilestoneConfig = connect(mapStateToProps, mapDispatchToProps)(_MilestoneConfig);
